import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal, Button, Select, Input, Checkbox, Spin } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import CompanyLocationService from "../../services/company-location.service";
import RouteCard from "./RouteCard";
import StoneSummary from "./StoneSummary";

// Task 2180 (F3) -- matches Diamond-PO-Prototype-v3.html's openShip()/shipPreview()/doShip()
// (lines 1002-1080) almost exactly: same fields, same validation order, same live summary text.
// Two deliberate deviations from the prototype's own demo data, not from its design: the
// prototype prefills Tracking no. with a random fake value (trk()) and Shipping cost with a
// fixed "65.00" -- fine for a mock, actively wrong for a real form (a supplier could ship with a
// meaningless tracking code without noticing). Both start empty here instead.
//
// React's controlled inputs already satisfy the plan's "live preview without losing focus"
// requirement for free -- the prototype's own version has to work around replacing innerHTML on
// every keystroke; nothing here re-keys or remounts an input, so typing never loses focus.

const { Option } = Select;

const CARRIERS = ["Malca-Amit", "Brink's", "FedEx — jewellers' block", "UPS — declared value"];

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const roundMoney = (v) => Math.round((v || 0) * 100) / 100;
const marginOf = (baseAmount, type, value) => {
  if (type === "PERCENT") return roundMoney((Number(baseAmount) || 0) * (Number(value) || 0) / 100);
  if (type === "AMOUNT") return roundMoney(Number(value) || 0);
  return 0;
};

const toRouteAddress = (loc) =>
  loc && {
    Label: loc.LocationName,
    ContactName: loc.ContactName,
    Address: loc.Address,
    City: loc.City,
    State: loc.State,
    Zip: loc.Zip,
    Country: loc.Country,
    Phone: loc.Phone,
  };

const toShipToRouteAddress = (snap) =>
  snap && {
    Label: snap.label,
    ContactName: snap.contact,
    Address: snap.line1,
    City: snap.city,
    State: snap.state,
    Zip: snap.zip,
    Country: snap.country,
    Phone: snap.phone,
  };

export const ShipModal = ({ docId, onClose, onDone }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [lines, setLines] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [fromAddressId, setFromAddressId] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [marginType, setMarginType] = useState("NONE");
  const [marginValue, setMarginValue] = useState("");
  const [showToRetailer, setShowToRetailer] = useState(false);
  const [carrier, setCarrier] = useState(CARRIERS[0]);
  const [trackingNo, setTrackingNo] = useState("");
  const [cost, setCost] = useState("");
  const [billTo, setBillTo] = useState("RETAILER");

  useEffect(() => {
    if (!docId || !dealerId) return;
    setLoading(true);
    Promise.all([
      PurchaseOrderService.GetDiamondDoc(docId, { DealerID: String(dealerId) }),
      CompanyLocationService.GetPOLocationDetails({ DealerID: dealerId }),
    ])
      .then(([docResponse, addrResponse]) => {
        setLoading(false);
        if (docResponse.data.status === true) {
          const d = docResponse.data.responseData;
          setDoc(d.Doc);
          const requested = (d.Lines || []).filter((l) => l.StatusCode === "REQUESTED");
          setLines(requested);
          setDecisions(Object.fromEntries(requested.map((l) => [l.DiamondLineID, "SHIP"])));
        } else {
          NotificationManager.error(t(docResponse.data.message || "Could not load document"));
        }
        if (addrResponse.data.message === "Success" && addrResponse.data.responseData?.Table) {
          const rows = addrResponse.data.responseData.Table.filter(
            (r) => r.LocationType === "LocationId2" && r.AddressKind === "SHIPPING" && (r.IsActive === true || r.IsActive === "1")
          );
          setAddresses(rows);
          const def = rows.find((r) => r.IsDefault === true || r.IsDefault === "1") || rows[0];
          if (def) setFromAddressId(def.LocationId);
        }
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("Could not load document"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, dealerId]);

  if (!docId) return null;
  if (loading) {
    return (
      <Modal visible onCancel={onClose} footer={null} title={t("Ship")}>
        <Spin spinning style={{ display: "block", padding: 40 }} />
      </Modal>
    );
  }
  if (!doc) return null;

  const isMemo = doc.DocKind === "MEMO";
  const shipToSnap = typeof doc.ShipToJson === "string" ? JSON.parse(doc.ShipToJson) : doc.ShipToJson;

  if (addresses.length === 0) {
    return (
      <Modal visible onCancel={onClose} footer={[<Button key="close" onClick={onClose}>{t("Close")}</Button>]} title={t("Add a shipping address first")}>
        <div style={{ background: "#fff7f6", border: "1px dashed #e2574c", borderRadius: 8, padding: 14, color: "#e2574c" }}>
          <b>{t("No ship-from address")}</b>
          <div>{t("A {{kind}} cannot ship without a sender address — it is printed on the document and the carrier label.", { kind: isMemo ? t("memo") : t("purchase order") })}</div>
        </div>
      </Modal>
    );
  }
  if (!shipToSnap) {
    return (
      <Modal visible onCancel={onClose} footer={[<Button key="close" onClick={onClose}>{t("OK")}</Button>]} title={t("Retailer has no receiving address")}>
        <p>{t("Ask the retailer to add one. The document cannot ship without it.")}</p>
      </Modal>
    );
  }

  const fromAddr = addresses.find((a) => a.LocationId === fromAddressId);
  const mt = isMemo && marginType !== "NONE" ? marginType : null;
  const mv = Number(marginValue) || 0;

  let shipCount = 0, laterCount = 0, declineCount = 0, declaredValue = 0;
  lines.forEach((l) => {
    const d = decisions[l.DiamondLineID];
    if (d === "SHIP") {
      shipCount++;
      declaredValue += Number(l.BaseAmount) + marginOf(l.BaseAmount, mt, mv);
    } else if (d === "LATER") laterCount++;
    else if (d === "DECLINE") declineCount++;
  });

  const costNum = Number(cost) || 0;

  const setDecision = (lineId, value) => setDecisions((prev) => ({ ...prev, [lineId]: value }));

  const handleSubmit = () => {
    if (!fromAddr) {
      NotificationManager.error(t("Both addresses are required"));
      return;
    }
    if (mt && !(mv > 0)) {
      NotificationManager.error(t("Enter a margin value or choose No margin"));
      return;
    }
    if (shipCount > 0 && !trackingNo.trim()) {
      NotificationManager.error(t("Tracking number is required"));
      return;
    }
    if (shipCount === 0 && declineCount === 0) {
      NotificationManager.error(t("At least one stone must be shipped or declined"));
      return;
    }

    const body = {
      DealerID: String(dealerId),
      FromAddressId: String(fromAddressId),
      Lines: lines.map((l) => ({ LineId: l.DiamondLineID, Decision: decisions[l.DiamondLineID] })),
      Margin: isMemo ? { Type: mt, Value: mv, ShowToRetailer: showToRetailer } : null,
      Shipping: { Carrier: carrier, TrackingNo: trackingNo.trim(), Cost: costNum, BillTo: billTo },
    };

    setSubmitting(true);
    PurchaseOrderService.ShipDiamondDoc(docId, body)
      .then((response) => {
        setSubmitting(false);
        if (response.data.status === true) {
          NotificationManager.success(shipCount ? t("Shipped") : t("Declined"));
          onDone && onDone();
          onClose();
          // Matches the prototype's doShip() -- always lands you on the document it just
          // shipped (openDoc(d.id)), whether you opened Ship from the Requests list or from
          // the document detail page itself.
          navigate(`/document/${docId}`);
        } else {
          NotificationManager.error(t(response.data.message || "Could not ship"));
        }
      })
      .catch(() => {
        setSubmitting(false);
        NotificationManager.error(t("Could not ship"));
      });
  };

  const goLabel = shipCount
    ? isMemo
      ? t("Ship {{n}} stone(s)", { n: shipCount })
      : t("Confirm & ship {{n}}", { n: shipCount })
    : declineCount
    ? t("Decline")
    : t("Ship");

  return (
    <Modal
      visible
      onCancel={onClose}
      width={760}
      title={isMemo ? t("Ship {{num}} to {{retailer}}", { num: doc.DocNumber, retailer: doc.RetailerName }) : t("Confirm & ship {{num}}", { num: doc.DocNumber })}
      footer={[
        <Button key="cancel" onClick={onClose}>{t("Cancel")}</Button>,
        <Button key="go" type="primary" loading={submitting} disabled={shipCount === 0 && declineCount === 0} onClick={handleSubmit}>
          {goLabel}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "#6b7689", fontWeight: 700, display: "block", marginBottom: 4 }}>
          {t("Ship from · your shipping address")}
        </label>
        <Select style={{ width: "100%" }} value={fromAddressId} onChange={setFromAddressId}>
          {addresses.map((a) => (
            <Option key={a.LocationId} value={a.LocationId}>
              {a.LocationName} — {a.City}
            </Option>
          ))}
        </Select>
      </div>

      <div style={{ marginBottom: 14 }}>
        <RouteCard
          fromAddress={toRouteAddress(fromAddr)}
          toAddress={toShipToRouteAddress(shipToSnap)}
          fromPartyName={t("You")}
          toPartyName={doc.RetailerName}
        />
      </div>

      <h4 style={{ margin: "0 0 8px" }}>{t("Stones")}</h4>
      <div style={{ overflowX: "auto", marginBottom: 14 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid #eee", color: "#6b7689", fontSize: 11, textTransform: "uppercase" }}>
              <th style={{ padding: "6px" }}>{t("Stone")}</th>
              <th style={{ padding: "6px" }}>{t("Decision")}</th>
              <th style={{ padding: "6px", textAlign: "right" }}>{t("Base")}</th>
              {isMemo && <th style={{ padding: "6px", textAlign: "right" }}>{t("Margin")}</th>}
              {isMemo && <th style={{ padding: "6px", textAlign: "right" }}>{t("Memo price")}</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const snap = typeof l.StoneSnapshotJson === "string" ? JSON.parse(l.StoneSnapshotJson) : l.StoneSnapshotJson;
              const d = decisions[l.DiamondLineID];
              const m = d === "SHIP" ? marginOf(l.BaseAmount, mt, mv) : 0;
              return (
                <tr key={l.DiamondLineID} style={{ borderBottom: "1px solid #f3f3f3" }}>
                  <td style={{ padding: "6px" }}>
                    <StoneSummary snap={snap} />
                  </td>
                  <td style={{ padding: "6px" }}>
                    <Select style={{ minWidth: 150 }} value={d} onChange={(v) => setDecision(l.DiamondLineID, v)}>
                      <Option value="SHIP">{t("Ship now")}</Option>
                      {isMemo && <Option value="LATER">{t("Hold for a later shipment")}</Option>}
                      <Option value="DECLINE">{t("Decline")}</Option>
                    </Select>
                  </td>
                  <td style={{ padding: "6px", textAlign: "right" }}>{fmtMoney(l.BaseAmount)}</td>
                  {isMemo && <td style={{ padding: "6px", textAlign: "right" }}>{d === "SHIP" && m ? "+" + fmtMoney(m) : "—"}</td>}
                  {isMemo && <td style={{ padding: "6px", textAlign: "right" }}><b>{fmtMoney(Number(l.BaseAmount) + m)}</b></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isMemo && (
        <>
          <h4 style={{ margin: "0 0 8px" }}>{t("Memo margin")}</h4>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 6 }}>
            <div>
              <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Type")}</label>
              <Select style={{ width: 150 }} value={marginType} onChange={setMarginType}>
                <Option value="NONE">{t("No margin")}</Option>
                <Option value="PERCENT">{t("% of base price")}</Option>
                <Option value="AMOUNT">{t("$ per stone")}</Option>
              </Select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Value")}</label>
              <Input style={{ width: 110 }} placeholder="0" value={marginValue} onChange={(e) => setMarginValue(e.target.value)} disabled={marginType === "NONE"} />
            </div>
            <Checkbox checked={showToRetailer} onChange={(e) => setShowToRetailer(e.target.checked)} style={{ paddingBottom: 8 }}>
              {t("Show the margin split to the retailer")}
            </Checkbox>
          </div>
          <div style={{ fontSize: 12, color: "#79838f", marginBottom: 14 }}>
            {t("Applied to each stone in this shipment and frozen on the line. If the retailer buys the stone, the purchase order uses the memo price.")}
          </div>
        </>
      )}

      <h4 style={{ margin: "0 0 8px" }}>{t("Shipping")}</h4>
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr .8fr 1fr", gap: 10, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Carrier")}</label>
          <Select style={{ width: "100%" }} value={carrier} onChange={setCarrier}>
            {CARRIERS.map((c) => (
              <Option key={c} value={c}>{c}</Option>
            ))}
          </Select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Tracking no.")}</label>
          <Input value={trackingNo} onChange={(e) => setTrackingNo(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Shipping cost $")}</label>
          <Input value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Charged to")}</label>
          <Select style={{ width: "100%" }} value={billTo} onChange={setBillTo}>
            <Option value="RETAILER">{t("Retailer")}</Option>
            <Option value="SUPPLIER">{t("Supplier absorbs")}</Option>
          </Select>
        </div>
      </div>

      <div
        style={{
          background: shipCount ? "#eef3ff" : "#fdf3e3",
          border: "1px solid " + (shipCount ? "#c9d7fb" : "#f0d9a8"),
          borderRadius: 8,
          padding: 12,
          fontSize: 13,
        }}
      >
        <b>
          {shipCount
            ? t("Shipping {{n}} stone(s) · declared {{value}}", { n: shipCount, value: fmtMoney(declaredValue) })
            : t("Nothing selected to ship")}
        </b>
        {laterCount > 0 && <div>{t("{{n}} held for a later shipment.", { n: laterCount })}</div>}
        {declineCount > 0 && <div>{t("{{n}} declined and released.", { n: declineCount })}</div>}
        {shipCount > 0 && (
          <div>
            {t("Shipping {{cost}} {{who}}", {
              cost: fmtMoney(costNum),
              who: billTo === "RETAILER" ? (isMemo ? t("charged to the retailer — billed on their first purchase off this memo.") : t("added to this purchase order.")) : t("absorbed by you."),
            })}
          </div>
        )}
        {shipCount > 0 && isMemo && <div>{t("These stones are unlisted everywhere the moment you ship.")}</div>}
      </div>
    </Modal>
  );
};

export default ShipModal;
